import { IContractValue } from "@components/global/new/ContractSelector/ContractSelector.props";
import { ErrorType } from "@components/global/new/Error/Error";
import {
  AddContractParams,
  AddContractResult,
  CreateContract,
  IPlanInContract,
} from "@services/contracts";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import useApiRequests from "@services/http/useApiRequest";
import { addContract } from "@store/contracts/action";
import { dev } from "@utils/dev";
import { generatePassword } from "@utils/generatePassword";
import { FormRawValues, getFormValue2 } from "@utils/getFormValue";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { useMiningPlans } from "@utils/hooks/useMiningPlans";
import { makeNotifs } from "@utils/makeNotifs";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ICreactContractProps } from "./CreateContractModal";
import { useUsers } from "@utils/hooks/useUsers";

interface Elems extends HTMLFormControlsCollection {
  [key: string]: any; // HTMLInputElement
}

interface MyForm extends HTMLFormElement {
  readonly elements: Elems;
}

export const useCreateContractControl = (props: ICreactContractProps) => {
  const dispatch = useDispatch();
  const { onSuccess } = useGlobalNotifs();
  const { enqueue } = useApiRequests();
  const { ok, errors } = makeNotifs();
  const [generated, setGenerated] = useState<string>();
  const [errorName, setErrorName] = useState<ErrorType>();
  const [errorPrice, setErrorPrice] = useState<ErrorType>();
  const [errorPass, setErrorPass] = useState<ErrorType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const plansCache = useMiningPlans();

  const users = useUsers();

  const onGenerate = () => {
    const gen = generatePassword();
    setGenerated(gen);
  };

  useEffect(() => {
    onGenerate();
  }, []);

  const resetErrors = () => {
    setErrorName(undefined);
    setErrorPrice(undefined);
    setErrorPass(undefined);
  };

  const checkErrors = (f: FormRawValues) => {
    let error;
    resetErrors();
    if (!f.name) {
      setErrorName({ ownError: [errors.fill] });
      error = errors.fill;
    }
    // if (!f.password) {
    //   setErrorPass({ ownError: errors.fill });
    //   error = errors.fill;
    // }
    // if (!f.consumption) {
    //   setErrorPrice({ ownError: errors.fill });
    //   error = errors.fill;
    // }

    return error;
  };

  const formControl = async (e: React.FormEvent<MyForm>) => {
    e.preventDefault();
    setIsLoading(true);
    const elems = e.currentTarget.elements;
    const value = getFormValue2(elems);

    try {
      const val: any = {
        name: value.values.name,
      };
      if (value.values.password) {
        val.password = value.values.password;
      }
      const error = checkErrors(value.values);
      if (error) return;

      const meta = {
        consumption: value.values.consumption,
      };

      const params: AddContractParams = {
        userId: Number(value.values.customer),
        name: value.values.name,
        password: value.values.password,
        planId: Number(value.values.plan),
        meta: JSON.stringify(meta)
      };

      const res = await enqueue<RecordResponse<AddContractResult>>(
        new CreateContract(params)
      );

      if (!res) return;

      if (res.Status !== "success") {
        setErrorName({
          ownError: [res.Message || "Unknown error"],
        });
        return;
      }
      const contractId = res.Record.id;
      const storePlans: IPlanInContract[] = [];
      const existsPlan = plansCache.find(
        (p) => p.id === res.Record.planId
      );
      if (existsPlan) {
        storePlans.push({
          id: existsPlan.id,
          name: existsPlan.name,
        });
      }
      const newContract = {
        ...params,
        id: contractId,
        files: "{}",
        plans: JSON.stringify(storePlans),
        meta: { ...meta, text: "" },
      } as IContractValue & { userId: number };
      dispatch(addContract(newContract));
      props.onResult?.(newContract);
      onSuccess(ok.success);
      props.handleDestroy();
      resetErrors();
    } finally {
      setIsLoading(false);
    }
  };

  return {
    generated,
    errors: {
      name: errorName,
      pass: errorPass,
      price: errorPrice,
    },
    isLoading,
    users,
    onGenerate,
    formControl,
  };
};
